/**
 * Lookup, plural choice and `{placeholder}` interpolation — framework-free, so
 * plain modules (toasts, label maps, formatters) translate the same way as
 * components. React goes through `useT` (re-renders on a locale switch).
 */
import { INTL_TAG, type Locale } from './config';
import { getBundleNs, getLocale } from './store';
import type { ArgsOf, MessageValue, Messages, Namespace, ParamValue, PluralForms } from './types';

/**
 * Declare an EN namespace. `ns` must equal the file name under `i18n/en/`
 * (the dictionaries spec checks it).
 *
 *   export default defineMessages('my-work', { title: 'My Work', … } as const);
 */
export function defineMessages<const M extends Messages>(ns: string, en: M): Namespace<M> {
  return { ns, en };
}

const pluralRules = new Map<Locale, Intl.PluralRules>();
function pluralRule(locale: Locale): Intl.PluralRules {
  let r = pluralRules.get(locale);
  if (!r) {
    r = new Intl.PluralRules(INTL_TAG[locale]);
    pluralRules.set(locale, r);
  }
  return r;
}

const numberFormats = new Map<Locale, Intl.NumberFormat>();
function formatNumber(n: number, locale: Locale): string {
  let f = numberFormats.get(locale);
  if (!f) {
    f = new Intl.NumberFormat(INTL_TAG[locale]);
    numberFormats.set(locale, f);
  }
  return f.format(n);
}

export function pickPlural(forms: PluralForms, count: number, locale: Locale): string {
  const cat = pluralRule(locale).select(count) as keyof PluralForms;
  return forms[cat] ?? forms.other;
}

function resolve(value: MessageValue, params: Readonly<Record<string, unknown>> | undefined, locale: Locale): string {
  if (typeof value === 'string') return value;
  const count = typeof params?.count === 'number' ? params.count : 0;
  return pickPlural(value, count, locale);
}

const PLACEHOLDER = /\{(\w+)\}/g;

export function interpolate(
  text: string,
  params: Readonly<Record<string, unknown>> | undefined,
  locale: Locale,
): string {
  if (!params) return text;
  return text.replace(PLACEHOLDER, (whole, name: string) => {
    const v = params[name];
    if (v === undefined || v === null) return whole;
    return typeof v === 'number' ? formatNumber(v, locale) : String(v);
  });
}

/** The raw message for `key` in `locale`, falling back to EN. */
export function lookup<M extends Messages>(ns: Namespace<M>, key: keyof M & string, locale: Locale): MessageValue {
  if (locale !== 'en') {
    const hit = getBundleNs(ns.ns, locale)?.[key];
    if (hit !== undefined) return hit;
  }
  return ns.en[key] ?? key;
}

export interface TFunction<M extends Messages> {
  <K extends keyof M & string>(key: K, ...args: ArgsOf<M[K]>): string;
  /** The current locale this function translates into. */
  readonly locale: Locale;
}

export function makeT<M extends Messages>(ns: Namespace<M>, locale: Locale): TFunction<M> {
  const t = (key: keyof M & string, params?: Readonly<Record<string, ParamValue>>): string =>
    interpolate(resolve(lookup(ns, key, locale), params, locale), params, locale);
  return Object.assign(t, { locale }) as unknown as TFunction<M>;
}

/**
 * Translate outside React, in the CURRENT locale. Fine for toasts, event
 * handlers and getters evaluated at render time; never cache the result in a
 * module-level constant (it would freeze the language at import time).
 */
export function tr<M extends Messages, K extends keyof M & string>(
  ns: Namespace<M>,
  key: K,
  ...args: ArgsOf<M[K]>
): string {
  return makeT(ns, getLocale())(key, ...args);
}

/**
 * A label map that reads the current locale on every access — a drop-in for
 * a module-level `Record<Id, string>` of labels:
 *
 *   export const ROLE_LABEL = labelMap(roles, { OWNER: 'owner', MEMBER: 'member' });
 *   ROLE_LABEL.OWNER  // "Владелец" in Russian
 */
export function labelMap<M extends Messages, Id extends string>(
  ns: Namespace<M>,
  keys: Readonly<Record<Id, keyof M & string>>,
): Readonly<Record<Id, string>> {
  const out = {} as Record<Id, string>;
  for (const id of Object.keys(keys) as Id[]) {
    Object.defineProperty(out, id, {
      enumerable: true,
      get: () => tr(ns, keys[id] as keyof M & string, ...([] as unknown as ArgsOf<M[keyof M & string]>)),
    });
  }
  return Object.freeze(out);
}
